/**
 * Delaware State-Specific Diagnostic Questions
 * All questions reference actual DE regulations, forms, and requirements
 * 
 * Regulatory Body: Delaware Department of Health and Social Services (DHSS)
 *                  Division of Health Care Quality (DHCQ)
 * 
 * PRIMARY REGULATIONS:
 * - 16 Del. Admin. Code 3350: Skilled Home Health Agencies (Licensure)
 * - 16 Del. Admin. Code 3351: Home Health Agencies–Aide Only (Licensure)
 * 
 * REGULATION STRUCTURE (3350):
 * - § 3350-1.0: Definitions
 * - § 3350-2.0: Licensing Requirements and Procedures
 * - § 3350-3.0: General Requirements
 * - § 3350-4.0: Governing Body
 * - § 3350-5.0: Administration/Personnel
 * - § 3350-6.0: Patient Care Management
 * - § 3350-7.0: Patient Rights
 * - § 3350-8.0: Quality Improvement
 * - § 3350-9.0: Insurance
 * - § 3350-10.0: Disaster Preparedness
 * - § 3350-11.0: Severability
 * 
 * KEY STATUTES:
 * - 16 Del. C. §122(3)(o): Home health agency licensing authority
 * - 16 Del. C. §§1145-1146: Criminal background checks; drug testing (home care)
 * - 16 Del. C. §§1131-1140: Abuse, neglect, mistreatment reporting
 * - 11 Del. C. §8564: Adult Abuse Registry
 * - 11 Del. C. §8563: Child Protection Registry check
 * 
 * TRAINING REQUIREMENTS (3350-5.7):
 * - 75 hours initial training (classroom + clinical)
 * - 12 hours annual in-service
 * - Annual competency evaluations
 * - Annual dementia-specific training (3350-5.7.12)
 * 
 * BACKGROUND CHECK SYSTEM:
 * - BCC (Background Check Center): backgroundcheckcenter.dhss.delaware.gov
 * - 60-day conditional employment permitted pending results
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const DELAWARE_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'DE-A1',
    itemId: 'A1',
    stateCode: 'DE',
    title: 'DHCQ Home Health Agency License Status',
    description: 'Delaware requires all home health agencies to be licensed by DHCQ under 16 Del. Admin. Code 3350',
    severity: 'high',
    regulationCode: '3350-2.0',
    regulationName: 'Licensing',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Delaware DHCQ?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses must be renewed annually with DHCQ. License fee is $300 annually; initial application fee is $500.',
      },
      {
        id: 'q2',
        question: 'What is your DHCQ license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DHCQ license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Delaware DHCQ Home Health Agency license is current and active per 16 Del. Admin. Code 3350.',
      atRisk: 'Your DHCQ license expires within 90 days. Delaware requires renewal applications to be submitted before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Delaware without a valid DHCQ license violates 16 Del. Admin. Code 3350. Contact DHCQ immediately at (302) 421-7400.',
      unknown: 'Verify your DHCQ license status by calling (302) 421-7400.',
    },
    stateNotes: [
      'DHCQ licenses are valid for 1 year and renewed annually',
      'Initial license fee: $500; Annual renewal: $300',
      'Initial license is provisional for 90 days pending on-site survey',
      'Medicare certification available through DHCQ survey',
    ],
  },

  {
    id: 'DE-A2',
    itemId: 'A2',
    stateCode: 'DE',
    title: 'Agency Type and Services',
    description: 'Delaware licenses skilled home health agencies (3350) and aide-only agencies (3351) separately',
    severity: 'high',
    regulationCode: '3350-1.0',
    regulationName: 'Definitions',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'What type of home health agency license do you hold?',
        inputType: 'select',
        options: [
          { value: 'skilled', label: 'Skilled Home Health Agency (3350) - nursing + aide services' },
          { value: 'aide_only', label: 'Home Health Agency–Aide Only (3351)' },
          { value: 'both', label: 'Both licenses' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'Skilled agencies provide nursing services; aide-only agencies provide personal care without skilled nursing',
      },
      {
        id: 'q2',
        question: 'Do you provide at least two home care services, one of which is licensed nursing or home health aide services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Definition of "home health agency" per 3350-1.0',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['skilled', 'aide_only', 'both'], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency type and services align with Delaware licensing requirements.',
      atRisk: 'Clarify your license type with DHCQ. Skilled agencies (3350) and aide-only agencies (3351) have different requirements.',
      nonCompliant: 'Your services may not match your license type. Contact DHCQ at (302) 421-7400.',
      unknown: 'Verify your license type with DHCQ.',
    },
    stateNotes: [
      '3350 = Skilled Home Health Agencies (nursing + aide services)',
      '3351 = Home Health Agencies–Aide Only (personal care without nursing)',
      'Must provide at least two home care services per 3350-1.0 definition',
    ],
  },

  {
    id: 'DE-A3',
    itemId: 'A3',
    stateCode: 'DE',
    title: 'Medicare/Medicaid Certification Status',
    description: 'Delaware requires proper certification for participation in Medicare/Medicaid programs',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare Certification',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency certified for Medicare and/or Delaware Medicaid?',
        inputType: 'select',
        options: [
          { value: 'both', label: 'Both Medicare and Medicaid certified' },
          { value: 'medicare_only', label: 'Medicare certified only' },
          { value: 'medicaid_only', label: 'Medicaid certified only' },
          { value: 'neither', label: 'Not certified for either program (private pay only)' },
        ],
        required: true,
        helpText: 'Certification is required to bill these programs',
      },
      {
        id: 'q2',
        question: 'When was your last DHCQ certification survey?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_to_3_years', label: '1-3 years ago' },
          { value: 'over_3_years', label: 'More than 3 years ago' },
          { value: 'never', label: 'Never surveyed' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'both', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['medicare_only', 'medicaid_only', 'neither'], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains appropriate certifications for the programs you participate in.',
      atRisk: 'Consider obtaining additional certifications to expand service capacity and patient access.',
      nonCompliant: 'Without Medicare/Medicaid certification, you cannot bill these programs. Contact DHCQ at (302) 421-7400 for certification requirements.',
      unknown: 'Verify your certification status with DHCQ.',
    },
    stateNotes: [
      'Medicare certification requires meeting 42 CFR 484 conditions of participation',
      'DHCQ conducts certification surveys',
      'Delaware Medicaid enrollment is separate from Medicare certification',
    ],
  },

  {
    id: 'DE-A4',
    itemId: 'A4',
    stateCode: 'DE',
    title: 'Governing Body Requirements',
    description: 'Delaware requires a governing body responsible for agency operations per 3350-4.0',
    severity: 'high',
    regulationCode: '3350-4.0',
    regulationName: 'Governing Body',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated governing body responsible for overall management?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which governing body functions are documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'bylaws', label: 'Bylaws or operating agreement' },
          { value: 'policies', label: 'Approval of policies and procedures' },
          { value: 'budget', label: 'Annual budget oversight' },
          { value: 'quality', label: 'Quality improvement oversight' },
          { value: 'appointments', label: 'Appointment of director/administrator' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body structure meets Delaware 3350-4.0 requirements.',
      atRisk: 'Governing body functions may be incomplete. Review 3350-4.0 requirements.',
      nonCompliant: 'No governing body is designated. This violates 3350-4.0.',
      unknown: 'Verify governing body requirements with DHCQ.',
    },
    stateNotes: [
      'Governing body may be individual owner, board, or other structure',
      '3350-4.0 requires oversight of management, policies, budget, and quality',
      'Must appoint and oversee director and clinical director',
    ],
  },

  {
    id: 'DE-A5',
    itemId: 'A5',
    stateCode: 'DE',
    title: 'Director Qualifications and Responsibilities',
    description: 'Delaware requires a qualified director responsible for day-to-day operations per 3350-5.1',
    severity: 'high',
    regulationCode: '3350-5.1',
    regulationName: 'Director',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated director who meets Delaware qualification requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Director must meet education and experience requirements per 3350-5.1',
      },
      {
        id: 'q2',
        question: 'Is there a written plan for delegation of responsibilities when the director is absent?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-5.1.3.5 requires written delegation plan',
      },
      {
        id: 'q3',
        question: 'Has the director designated a similarly qualified person to act in their absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, in writing' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-5.1.4 requires written designation',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Director qualifications and succession planning meet Delaware 3350-5.1 requirements.',
      atRisk: 'Director designation or succession planning may be incomplete. Review 3350-5.1.',
      nonCompliant: 'No qualified director is designated. This violates 3350-5.1.',
      unknown: 'Verify director qualifications meet 3350-5.1.',
    },
    stateNotes: [
      'Director must notify DHCQ within 30 days of any change in director or clinical director (3350-3.4)',
      'Director reviews policies annually and reports to governing body (3350-5.1.3.8)',
    ],
  },

  {
    id: 'DE-A6',
    itemId: 'A6',
    stateCode: 'DE',
    title: 'Clinical Director Requirements',
    description: 'Delaware requires a clinical director responsible for patient care services per 3350-5.2',
    severity: 'high',
    regulationCode: '3350-5.2',
    regulationName: 'Clinical Director',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated clinical director?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the clinical director a registered nurse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical director meets Delaware 3350-5.2 requirements.',
      atRisk: 'Clinical director qualifications should be verified against 3350-5.2.',
      nonCompliant: 'No clinical director is designated. This violates 3350-5.2.',
      unknown: 'Verify clinical director qualifications.',
    },
    stateNotes: [
      'Clinical director must be a registered nurse per 3350-5.2',
      'Verify RN license with Delaware Board of Nursing',
      'Responsible for patient care services oversight',
    ],
  },

  {
    id: 'DE-A7',
    itemId: 'A7',
    stateCode: 'DE',
    title: 'Written Policies and Procedures',
    description: 'Delaware requires specific written policies per 3350-5.4',
    severity: 'medium',
    regulationCode: '3350-5.4',
    regulationName: 'Policies',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Delaware-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities (3350-5.4.2.1)' },
          { value: 'incidents', label: 'Incidents, accidents, and medical emergencies (3350-5.4.2.2)' },
          { value: 'background', label: 'Criminal background check procedures' },
          { value: 'supervision', label: 'Clinical supervision requirements' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'training', label: 'Staff training requirements' },
          { value: 'infection', label: 'Infection control policies' },
          { value: 'abuse_reporting', label: 'Abuse and neglect reporting (16 Del. C. §1132)' },
          { value: 'emergency', label: 'Emergency preparedness' },
          { value: 'advance_directive', label: 'Advance health-care directives (16 Del. C. Ch. 25)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Delaware 3350-5.4 requirements.',
      atRisk: 'Your policy manual is missing required sections per 3350-5.4. DHCQ surveyors will review policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per 3350-5.4. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against 3350-5.4 requirements.',
    },
    stateNotes: [
      'Policy manuals must be prepared and followed (3350-5.4.1)',
      'Incident reports must be kept on file for minimum 6 years (3350-3.3)',
      'Policies must be reviewed annually by director (3350-5.1.3.8)',
    ],
  },

  {
    id: 'DE-A8',
    itemId: 'A8',
    stateCode: 'DE',
    title: 'Insurance Requirements',
    description: 'Delaware requires home health agencies to maintain proper insurance per 3350-9.0',
    severity: 'high',
    regulationCode: '3350-9.0',
    regulationName: 'Insurance',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain current professional liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your professional liability coverage amount?',
        inputType: 'select',
        options: [
          { value: '1m_3m', label: '$1 million/$3 million or higher' },
          { value: 'below_1m', label: 'Below $1 million' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: '1m_3m', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['below_1m', 'unsure'], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains adequate professional liability insurance per 3350-9.0.',
      atRisk: 'Review your liability coverage amounts with your insurance provider to ensure adequate protection.',
      nonCompliant: 'Operating without professional liability insurance violates 3350-9.0. Obtain coverage immediately.',
      unknown: 'Verify your professional liability insurance coverage.',
    },
    stateNotes: [
      '3350-9.0 requires professional liability insurance',
      'Recommended minimum: $1 million/$3 million coverage',
      'Proof of insurance may be required during licensing',
    ],
  },

  {
    id: 'DE-A9',
    itemId: 'A9',
    stateCode: 'DE',
    title: 'Workers Compensation Insurance',
    description: 'Delaware requires employers to maintain workers compensation insurance per 19 Del. C. Ch. 23',
    severity: 'high',
    regulationCode: '19 Del. C. 23',
    regulationName: 'Workers Compensation',
    agencyName: 'Delaware Department of Labor',
    agencyAbbr: 'DOL',
    agencyPhone: '(302) 761-8200',
    agencyUrl: 'https://labor.delaware.gov/divisions/industrial-affairs/workers-compensation/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your workers compensation insurance meets Delaware 19 Del. C. Ch. 23 requirements.',
      atRisk: 'Verify your workers compensation policy is current.',
      nonCompliant: 'Operating without workers compensation insurance violates 19 Del. C. Ch. 23. Contact Delaware Department of Labor.',
      unknown: 'Verify workers compensation insurance status.',
    },
    stateNotes: [
      '19 Del. C. Ch. 23 governs workers compensation requirements',
      'Most employers with 1 or more employees must carry coverage',
      'Delaware Department of Labor: (302) 761-8200',
    ],
  },

  {
    id: 'DE-A10',
    itemId: 'A10',
    stateCode: 'DE',
    title: 'Record Retention Requirements',
    description: 'Delaware requires minimum 6 years record retention per 3350-3.3',
    severity: 'medium',
    regulationCode: '3350-3.3',
    regulationName: 'Record Retention',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer (meets DE requirement)' },
          { value: '4to5', label: '4-5 years' },
          { value: 'less4', label: 'Less than 4 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Delaware 3350-3.3 requires retention for at least 6 years',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '6plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '4to5', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less4', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Delaware\'s 6-year requirement under 3350-3.3.',
      atRisk: 'Delaware requires 6-year record retention per 3350-3.3. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Delaware\'s 6-year requirement under 3350-3.3. Immediately update your retention policy.',
      unknown: 'Review record retention requirements under 3350-3.3.',
    },
    stateNotes: [
      '3350-3.3 requires minimum 6 years record retention',
      'Patient records, personnel files, and incident reports all require 6-year retention',
      'Medicare requires 5 years from cost report filing',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'DE-B1',
    itemId: 'B1',
    stateCode: 'DE',
    title: 'Personnel File Requirements',
    description: 'Delaware requires specific documents in each employee\'s personnel file per 3350-5.5 and 3350-5.6',
    severity: 'high',
    regulationCode: '3350-5.5',
    regulationName: 'Personnel Records',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application with work history' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'license', label: 'Professional license verification (if applicable)' },
          { value: 'bcc', label: 'BCC criminal background check clearance' },
          { value: 'drug', label: 'Drug screening results' },
          { value: 'references', label: 'Reference verification' },
          { value: 'health', label: 'Health clearance/physical exam' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'orientation', label: 'Orientation and training documentation' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'competency', label: 'Annual competency evaluation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Delaware-required documentation per 3350-5.5 and 3350-5.6.',
      atRisk: 'Some personnel files are missing required documents. Delaware requires complete documentation before staff provide care.',
      nonCompliant: 'Personnel files are missing critical documentation required by 3350-5.5. DHCQ surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against 3350-5.5 requirements.',
    },
    stateNotes: [
      '3350-5.5 and 3350-5.6 list required personnel file documents',
      'BCC clearance documentation required',
      'Annual competency evaluations must be documented',
      'DHCQ surveyors review personnel files during surveys',
    ],
  },

  {
    id: 'DE-B2',
    itemId: 'B2',
    stateCode: 'DE',
    title: 'Professional License Verification',
    description: 'Delaware requires verification of all professional licenses per 3350-6.6.2.2',
    severity: 'high',
    regulationCode: '3350-6.6.2.2',
    regulationName: 'Qualifications',
    agencyName: 'Delaware Division of Professional Regulation',
    agencyAbbr: 'DPR',
    agencyPhone: '(302) 744-4500',
    agencyUrl: 'https://dpr.delaware.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses with the Delaware Division of Professional Regulation before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: '3350-6.6.2.2 requires maintenance of verification of current licensure/registration',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets Delaware requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification to catch expired licenses.',
      nonCompliant: 'No license verification process violates 3350-6.6.2.2. Implement verification with DPR immediately.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      '3350-6.6.2.2 requires verification of licensure/registration',
      'Delaware Division of Professional Regulation: dpr.delaware.gov',
      'Track license expiration dates for all licensed staff',
    ],
  },

  {
    id: 'DE-B3',
    itemId: 'B3',
    stateCode: 'DE',
    title: 'I-9 Employment Eligibility Verification',
    description: 'Federal law requires employment eligibility verification for all employees',
    severity: 'high',
    regulationCode: '8 U.S.C. 1324a',
    regulationName: 'I-9 Verification',
    agencyName: 'U.S. Citizenship and Immigration Services',
    agencyAbbr: 'USCIS',
    agencyPhone: '(800) 375-5283',
    agencyUrl: 'https://www.uscis.gov/i-9',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees have completed I-9 forms on file within 3 days of hire?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees' },
          { value: 'most', label: 'Most employees, some may be missing' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'I-9 employment eligibility verification is properly maintained.',
      atRisk: 'Some I-9 forms may be missing. Audit all employee files for compliance.',
      nonCompliant: 'Missing I-9 forms violate federal law. Complete forms for all employees immediately.',
      unknown: 'Review I-9 compliance status.',
    },
    stateNotes: [
      'I-9 required by 8 U.S.C. 1324a for all employees',
      'Section 1 must be completed by employee within 3 days of hire',
      'Section 2 must be completed by employer within 3 days of hire',
      'Forms available at uscis.gov/i-9',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'DE-C1',
    itemId: 'C1',
    stateCode: 'DE',
    title: 'BCC Criminal Background Check (16 Del. C. §1145)',
    description: 'Delaware requires background checks through the BCC (Background Check Center) per 16 Del. C. §1145',
    severity: 'high',
    regulationCode: '16 Del. C. 1145',
    regulationName: 'Background Check',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://backgroundcheckcenter.dhss.delaware.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you use the Delaware Background Check Center (BCC) for all new direct care employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees go through BCC' },
          { value: 'most', label: 'Most do, some may bypass' },
          { value: 'no', label: 'No / Not using BCC' },
        ],
        required: true,
        helpText: 'BCC is the electronic portal combining SBI, FBI, Adult Abuse Registry, and other checks',
      },
      {
        id: 'q2',
        question: 'Which background checks are completed through BCC? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'sbi', label: 'Delaware SBI state criminal check' },
          { value: 'fbi', label: 'FBI national fingerprint-based check' },
          { value: 'adult_abuse', label: 'Adult Abuse Registry (11 Del. C. §8564)' },
          { value: 'child_protection', label: 'Child Protection Registry (11 Del. C. §8563)' },
          { value: 'drug', label: 'Drug screening' },
        ],
        helpText: 'BCC aggregates data from multiple sources',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'BCC background check process meets 16 Del. C. §1145 requirements.',
      atRisk: 'BCC process may have gaps. Ensure ALL direct care employees complete full background check.',
      nonCompliant: 'Failure to use BCC violates 16 Del. C. §1145. Register at backgroundcheckcenter.dhss.delaware.gov. Civil penalties of $1,000-$5,000 per violation.',
      unknown: 'Contact DHCQ at (302) 421-7400 for BCC requirements.',
    },
    stateNotes: [
      'BCC combines SBI, FBI, Adult Abuse Registry, and other data sources',
      'Fingerprinting required through SBI',
      'Civil penalty: $1,000-$5,000 for employing without background check',
      'Drug screening also required under 16 Del. C. §1146',
    ],
  },

  {
    id: 'DE-C2',
    itemId: 'C2',
    stateCode: 'DE',
    title: 'Conditional Employment Period',
    description: 'Delaware permits 60-day conditional employment pending background check results',
    severity: 'high',
    regulationCode: '16 Del. C. 1145',
    regulationName: 'Conditional Employment',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/bgchks/',
    questions: [
      {
        id: 'q1',
        question: 'Do any staff begin working with patients before full BCC clearance is received?',
        inputType: 'select',
        options: [
          { value: 'never', label: 'No - always wait for full clearance' },
          { value: 'conditional', label: 'Yes - conditional employment with fingerprint verification' },
          { value: 'no_check', label: 'Yes - without any verification' },
        ],
        required: true,
        helpText: '16 Del. C. §1145(e) allows 60-day conditional employment if fingerprinting is verified',
      },
      {
        id: 'q2',
        question: 'If using conditional employment, do you verify fingerprinting completion before allowing patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always verify fingerprint submission' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'conditional',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before background check clearance without verification (direct violation)
      { condition: 'equals', questionId: 'q1', value: 'no_check', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'conditional' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'conditional' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Conditional employment process meets 16 Del. C. §1145(e) requirements.',
      atRisk: 'Conditional employment requires fingerprint verification. Ensure compliance.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance without proper verification. Per 16 Del. C. §1145, NO patient contact is allowed without fingerprint verification. Civil penalties of $1,000-$5,000 per violation apply.',
      unknown: 'Review conditional employment procedures.',
    },
    stateNotes: [
      '16 Del. C. §1145(e) permits 60-day conditional employment',
      'Fingerprinting must be verified before patient contact',
      'Full BCC clearance required within 60 days',
      'Direct supervision recommended during conditional period',
    ],
  },

  {
    id: 'DE-C3',
    itemId: 'C3',
    stateCode: 'DE',
    title: 'Adult Abuse Registry Check',
    description: 'Delaware requires Adult Abuse Registry checks for all direct care staff per 11 Del. C. §8564',
    severity: 'high',
    regulationCode: '11 Del. C. 8564',
    regulationName: 'Abuse Registry',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/adult-abuse-registry/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Delaware Adult Abuse Registry for all new hires through BCC?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Adult Abuse Registry is included in BCC background check',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Adult Abuse Registry check process meets Delaware 11 Del. C. §8564 requirements.',
      atRisk: 'Adult Abuse Registry check may not be completed. Verify BCC includes this check.',
      nonCompliant: 'Failure to check Adult Abuse Registry violates 11 Del. C. §8564. Civil penalties of $1,000-$5,000 per violation.',
      unknown: 'Verify Adult Abuse Registry check procedures.',
    },
    stateNotes: [
      'Registry includes findings of abuse, neglect, mistreatment, financial exploitation',
      'Individuals on registry with substantiated findings cannot be employed',
      'Civil penalty: $1,000-$5,000 for failure to check',
    ],
  },

  {
    id: 'DE-C4',
    itemId: 'C4',
    stateCode: 'DE',
    title: 'Drug Screening Requirements',
    description: 'Delaware requires drug testing for all direct care employees per 16 Del. C. §1146',
    severity: 'high',
    regulationCode: '16 Del. C. 1146',
    regulationName: 'Drug Testing',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/bgchks/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct pre-employment drug testing for all direct care staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '16 Del. C. §1146 requires drug testing for home care agency employees',
      },
      {
        id: 'q2',
        question: 'Do you have a drug-free workplace policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Drug testing program meets Delaware 16 Del. C. §1146 requirements.',
      atRisk: 'Drug-free workplace policy should be documented. Review your policies.',
      nonCompliant: 'Failure to conduct drug testing violates 16 Del. C. §1146.',
      unknown: 'Verify drug testing procedures.',
    },
    stateNotes: [
      '16 Del. C. §1146 requires drug testing for home care employees',
      'Pre-employment testing required',
      'Drug-free workplace policy recommended',
      'Testing may be included in BCC process',
    ],
  },

  {
    id: 'DE-C5',
    itemId: 'C5',
    stateCode: 'DE',
    title: 'Disqualifying Offenses',
    description: 'Delaware has specific disqualifying offenses that prevent employment',
    severity: 'high',
    regulationCode: '16 Del. C. 1145',
    regulationName: 'Disqualifying Offenses',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/bgchks/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy listing Delaware disqualifying offenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Disqualifying offenses include homicide (no time limit), sexual felonies (no time limit), violent felonies (10 years), drug felonies (10 years)',
      },
      {
        id: 'q2',
        question: 'Do you screen all BCC results against the disqualifying offense list?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disqualifying offense screening process meets Delaware requirements.',
      atRisk: 'Background check review may not include all disqualifying offenses. Review your screening criteria.',
      nonCompliant: 'No written disqualifying offense policy exists. Develop policy immediately.',
      unknown: 'Review disqualifying offense screening procedures.',
    },
    stateNotes: [
      'Homicide offenses: no time limit',
      'Sexual felonies: no time limit',
      'Violent felonies (11 Del. C. §4201(c)): 10 years',
      'Drug felonies: 10 years',
      'Other felonies: 5 years',
      'Drug misdemeanors: 5 years',
      'Class A misdemeanors (11 Del. C. Ch. 5, Subch. II, Subpt. A): 5 years',
    ],
  },

  {
    id: 'DE-C6',
    itemId: 'C6',
    stateCode: 'DE',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to check OIG List of Excluded Individuals/Entities',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list before hiring and monthly thereafter?',
        inputType: 'select',
        options: [
          { value: 'hire_monthly', label: 'Yes, before hire and monthly' },
          { value: 'hire_only', label: 'Yes, before hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'hire_monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed before hire and monthly.',
      atRisk: 'OIG exclusion list should be checked monthly, not just at hire. Implement monthly screening.',
      nonCompliant: 'OIG exclusion list checks are required for Medicare/Medicaid participation. Employing excluded individuals can result in penalties.',
      unknown: 'Check the OIG exclusion list at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG LEIE check required under 42 CFR 1001.1901',
      'Check at exclusions.oig.hhs.gov',
      'Monthly screening is federal best practice',
      'Also check SAM.gov exclusion list',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'DE-D1',
    itemId: 'D1',
    stateCode: 'DE',
    title: 'TB Screening Requirements',
    description: 'Delaware requires TB screening for all staff with patient contact',
    severity: 'high',
    regulationCode: '3350-5.5',
    regulationName: 'Health Requirements',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff with patient contact have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files and complete screenings.',
      nonCompliant: 'Staff are working without valid TB screening. This creates health risks.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      '3350-5.5 requires TB screening for patient contact staff',
      'Baseline screening at hire required',
      'IGRA blood test or TST acceptable',
      'Follow CDC guidelines for healthcare workers',
    ],
  },

  {
    id: 'DE-D2',
    itemId: 'D2',
    stateCode: 'DE',
    title: 'Hepatitis B Vaccination Program',
    description: 'OSHA requires Hepatitis B vaccination be offered to employees with occupational exposure',
    severity: 'high',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(215) 861-4900',
    agencyUrl: 'https://www.osha.gov/bloodborne-pathogens',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to all employees with potential blood/OPIM exposure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document employee vaccination status or signed declination forms?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA 29 CFR 1910.1030 requirements.',
      atRisk: 'Hepatitis B vaccination documentation is incomplete. Ensure all employees have vaccination records or declination forms.',
      nonCompliant: 'Failure to offer Hepatitis B vaccination violates OSHA 29 CFR 1910.1030. Implement vaccination program immediately.',
      unknown: 'Review Hepatitis B vaccination program.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 (Bloodborne Pathogens Standard)',
      'Vaccination must be offered free to employees with occupational exposure',
      'Employees may decline but must sign declination form',
      'Delaware OSHA Region 3: (215) 861-4900',
    ],
  },

  {
    id: 'DE-D3',
    itemId: 'D3',
    stateCode: 'DE',
    title: 'Infection Control Program',
    description: 'Delaware requires comprehensive infection control per 3350-5.4',
    severity: 'high',
    regulationCode: '3350-5.4',
    regulationName: 'Infection Control',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'waste', label: 'Medical waste disposal procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets Delaware requirements.',
      atRisk: 'Infection control program is missing some components. Review requirements.',
      nonCompliant: 'Infection control program is inadequate. Develop a comprehensive program immediately.',
      unknown: 'Review infection control program.',
    },
    stateNotes: [
      '3350-5.4 requires infection control policies',
      'Must include written policies, staff training, PPE',
      'Hand hygiene and surveillance essential components',
      'Medical waste disposal per Delaware regulations',
    ],
  },

  {
    id: 'DE-D4',
    itemId: 'D4',
    stateCode: 'DE',
    title: 'Incident and Injury Reporting',
    description: 'Delaware requires documentation of incidents, accidents, and medical emergencies per 3350-5.4.2.2',
    severity: 'high',
    regulationCode: '3350-5.4.2.2',
    regulationName: 'Incident Reporting',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies for handling and documenting incidents, accidents, and medical emergencies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3350-5.4.2.2 requires written policies and 6-year retention of reports',
      },
      {
        id: 'q2',
        question: 'Do you keep incident reports on file for at least 6 years?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-3.3 requires minimum 6-year retention',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting program meets Delaware 3350-5.4.2.2 requirements.',
      atRisk: 'Incident report retention may not meet 6-year requirement. Review your retention policy.',
      nonCompliant: 'No incident reporting policy exists. Develop policy per 3350-5.4.2.2.',
      unknown: 'Review incident reporting procedures.',
    },
    stateNotes: [
      '3350-5.4.2.2 requires written incident policies',
      'Incident reports must be retained for 6 years (3350-3.3)',
      'Include accidents, injuries, and medical emergencies',
      'Document investigation and corrective actions',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'DE-E1',
    itemId: 'E1',
    stateCode: 'DE',
    title: 'Home Health Aide Initial Training (75 Hours)',
    description: 'Delaware requires 75 hours initial training for home health aides per 3350-5.7.4',
    severity: 'high',
    regulationCode: '3350-5.7.4',
    regulationName: 'Aide Training',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do all home health aides complete the required 75 hours of training before patient care?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 75 hours' },
          { value: 'most', label: 'Most do, some may be incomplete' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: '3350-5.7.4 requires minimum 75 hours including instruction and supervised practicum',
      },
      {
        id: 'q2',
        question: 'Does your training program include all required topics?',
        inputType: 'checklist',
        options: [
          { value: 'adl', label: 'Assisting patients with activities of daily living' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'communication', label: 'Communication and observation skills' },
          { value: 'infection', label: 'Infection control' },
          { value: 'documentation', label: 'Documentation and reporting' },
          { value: 'practicum', label: 'Supervised clinical practicum' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial training program meets Delaware 3350-5.7.4 requirements (75 hours).',
      atRisk: 'Training program may be incomplete. Verify 75 hours with required curriculum.',
      nonCompliant: 'Aides are providing services without required training. This violates 3350-5.7.4.',
      unknown: 'Review training program against 3350-5.7.4 requirements.',
    },
    stateNotes: [
      '75 hours minimum including instruction and supervised practicum',
      'Training supervised by RN with 1+ year home health experience (3350-5.7.1)',
      'Aides with 2+ year break in service must repeat training or demonstrate competency (3350-5.7.5)',
    ],
  },

  {
    id: 'DE-E2',
    itemId: 'E2',
    stateCode: 'DE',
    title: 'Orientation Training',
    description: 'Delaware requires comprehensive orientation for all employees per 3350-5.7.2',
    severity: 'high',
    regulationCode: '3350-5.7.2',
    regulationName: 'Orientation',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new employees/contractors complete orientation before patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3350-5.7.2 requires all employees/contractors to complete orientation',
      },
      {
        id: 'q2',
        question: 'Does your orientation program include required topics? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'structure', label: 'Agency structure and organization' },
          { value: 'population', label: 'Population served' },
          { value: 'philosophy', label: 'Patient care philosophy' },
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'abuse', label: 'Abuse recognition and reporting (16 Del. C. §1132)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation program meets Delaware 3350-5.7.2 and 3350-5.7.3 requirements.',
      atRisk: 'Orientation program may be missing required topics. Review curriculum.',
      nonCompliant: 'Staff are providing services without orientation. This violates 3350-5.7.2.',
      unknown: 'Review orientation program requirements.',
    },
    stateNotes: [
      '3350-5.7.2 requires orientation for all employees/contractors',
      '3350-5.7.3 lists required orientation topics',
      'Must include abuse reporting (16 Del. C. §1132)',
      'Complete before patient contact',
    ],
  },

  {
    id: 'DE-E3',
    itemId: 'E3',
    stateCode: 'DE',
    title: 'Annual In-Service Training (12 Hours)',
    description: 'Delaware requires 12 hours annual continuing education for aides per 3350-5.7.6',
    severity: 'high',
    regulationCode: '3350-5.7.6',
    regulationName: 'Annual Training',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL aides complete at least 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: '3350-5.7.6 requires at least 12 hours annually',
      },
      {
        id: 'q2',
        question: 'Does annual training include required topics? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'adl', label: 'Assisting patients to achieve maximum self-reliance' },
          { value: 'environment', label: 'Maintaining clean, healthy, safe environment' },
          { value: 'referral', label: 'Items requiring referral to agency' },
          { value: 'observation', label: 'Observation, reporting, and documentation' },
        ],
        helpText: '3350-5.7.6 specifies required topics',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets Delaware 3350-5.7.6 requirements (12 hours).',
      atRisk: 'Some aides may be behind on annual training. Review records and schedule catch-up training.',
      nonCompliant: 'No structured annual training program exists. Implement per 3350-5.7.6.',
      unknown: 'Review annual training requirements.',
    },
    stateNotes: [
      '12 hours minimum annually per 3350-5.7.6',
      'Must include topics specified in 3350-5.7.6',
      'Track training hours per employee',
      'Document in personnel file',
    ],
  },

  {
    id: 'DE-E4',
    itemId: 'E4',
    stateCode: 'DE',
    title: 'Annual Dementia Training',
    description: 'Delaware requires annual dementia-specific training per 3350-5.7.12',
    severity: 'high',
    regulationCode: '3350-5.7.12',
    regulationName: 'Dementia Training',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees/contractors providing direct patient care complete annual dementia-specific training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete annual dementia training' },
          { value: 'most', label: 'Most do, some may be behind' },
          { value: 'no', label: 'No dementia-specific training program' },
        ],
        required: true,
        helpText: '3350-5.7.12 requires annual dementia training for all direct care staff',
      },
      {
        id: 'q2',
        question: 'Does your dementia training include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'communication', label: 'Communicating with persons diagnosed with Alzheimer\'s or dementia' },
          { value: 'needs', label: 'Psychological, social, and physical needs of those persons' },
          { value: 'safety', label: 'Safety measures for persons with dementia' },
        ],
        helpText: '3350-5.7.12 specifies three required elements',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual dementia training meets Delaware 3350-5.7.12 requirements.',
      atRisk: 'Some staff may be behind on dementia training. Review records and schedule training.',
      nonCompliant: 'No dementia training program exists. This violates 3350-5.7.12. Implement immediately.',
      unknown: 'Review dementia training requirements per 3350-5.7.12.',
    },
    stateNotes: [
      'Required annually for ALL employees/contractors providing direct patient care',
      'Must include: communication, psychological/social/physical needs, safety measures',
      'This is a Delaware-specific requirement beyond federal standards',
    ],
  },

  {
    id: 'DE-E5',
    itemId: 'E5',
    stateCode: 'DE',
    title: 'Competency Evaluation',
    description: 'Delaware requires initial and annual competency evaluations per 3350-5.7.9',
    severity: 'high',
    regulationCode: '3350-5.7.9',
    regulationName: 'Competency',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees/contractors pass a competency evaluation before providing patient care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3350-5.7.9 requires competency evaluation prior to providing care AND annually',
      },
      {
        id: 'q2',
        question: 'Do you conduct annual competency evaluations for all direct care staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, annually' },
          { value: 'no', label: 'No / Initial only' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluation program meets Delaware 3350-5.7.9 requirements (initial + annual).',
      atRisk: 'Annual competency evaluations are required. Implement annual evaluations.',
      nonCompliant: 'Staff are providing care without competency evaluation. This violates 3350-5.7.9.',
      unknown: 'Review competency evaluation requirements.',
    },
    stateNotes: [
      '3350-5.7.9 requires initial competency evaluation before patient care',
      'Annual competency evaluations required',
      'RN must conduct or supervise evaluations',
      'Document results in personnel file',
    ],
  },

  {
    id: 'DE-E6',
    itemId: 'E6',
    stateCode: 'DE',
    title: 'Abuse Reporting Training',
    description: 'Delaware requires training on mandatory abuse reporting per 16 Del. C. §1132',
    severity: 'high',
    regulationCode: '16 Del. C. 1132',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on identifying and reporting suspected abuse, neglect, mistreatment, or financial exploitation?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to DHCQ and the appropriate authorities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, reporting procedures are documented and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff are properly trained on mandatory abuse reporting per 16 Del. C. §1132.',
      atRisk: 'Abuse reporting training may be insufficient. Ensure ongoing training and documented procedures.',
      nonCompliant: 'Failure to train on mandatory reporting violates 16 Del. C. §1132. Criminal penalties apply for failure to report.',
      unknown: 'Verify abuse reporting training meets 16 Del. C. §1132 requirements.',
    },
    stateNotes: [
      'Mandatory reporters must report abuse, neglect, mistreatment, financial exploitation',
      'Adult Protective Services Hotline: 1-888-277-4302 (1-888-APS-4302) - 24 hours',
      '16 Del. C. §1132 specifies healthcare facility reporting requirements',
      'Title 31, Chapter 39 (§§ 3901-3913) governs general Adult Protective Services',
      '16 Del. C. §1137: Failure to report is Class A misdemeanor (first offense), Class G felony (subsequent)',
      'Reports may be made anonymously; reporter identity is confidential',
    ],
  },

  {
    id: 'DE-E7',
    itemId: 'E7',
    stateCode: 'DE',
    title: 'Training Documentation',
    description: 'Delaware requires documentation of all training activities per 3350-5.7.7 and 3350-5.7.11',
    severity: 'medium',
    regulationCode: '3350-5.7.7',
    regulationName: 'Training Records',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Which training documentation do you maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dates_hours', label: 'Dates and hours of training' },
          { value: 'content', label: 'Training content/topics covered' },
          { value: 'trainer', label: 'Name and title of person providing training' },
          { value: 'attendance', label: 'Attendance records' },
          { value: 'competency', label: 'Competency evaluation results' },
        ],
        required: true,
        helpText: '3350-5.7.7 specifies required documentation elements',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training documentation meets Delaware 3350-5.7.7 requirements.',
      atRisk: 'Training documentation may be incomplete. Review documentation requirements.',
      nonCompliant: 'Training documentation is inadequate per 3350-5.7.7. Implement comprehensive records.',
      unknown: 'Review training documentation practices.',
    },
    stateNotes: [
      '3350-5.7.7 specifies training documentation requirements',
      'Include dates, hours, content, trainer, and attendance',
      'Competency evaluation results must also be documented',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'DE-F1',
    itemId: 'F1',
    stateCode: 'DE',
    title: 'Patient Care Management',
    description: 'Delaware requires comprehensive patient care management per 3350-6.0',
    severity: 'high',
    regulationCode: '3350-6.0',
    regulationName: 'Care Management',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for every patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'All home health agency services must be supervised by a registered nurse. Is this requirement met?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per Delaware statute, all HHA services must be RN-supervised',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient care management meets Delaware 3350-6.0 requirements.',
      atRisk: 'Care plans may be incomplete or delayed. Ensure plans are in place before services begin.',
      nonCompliant: 'Services are being provided without proper care planning or RN supervision. This violates Delaware requirements.',
      unknown: 'Review patient care management against 3350-6.0 requirements.',
    },
    stateNotes: [
      '3350-6.0 governs patient care management requirements',
      'Written care plan required before services begin',
      'All HHA services must be RN-supervised',
      'Care plan must be individualized to patient needs',
    ],
  },

  {
    id: 'DE-F2',
    itemId: 'F2',
    stateCode: 'DE',
    title: 'Supervisory Visits for Home Health Aide Services',
    description: 'Delaware requires on-site RN supervisory visits for aide services per 3350-6.6.4.2',
    severity: 'high',
    regulationCode: '3350-6.6.4.2',
    regulationName: 'Supervisory Visits',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do RNs conduct on-site supervisory visits for all patients receiving home health aide services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3350-6.6.4.2 requires on-site professional supervisory visits',
      },
      {
        id: 'q2',
        question: 'How often do supervisory visits occur?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 days or more frequently' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly or less' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_14', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'quarterly', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervisory visit frequency meets Delaware 3350-6.6.4.2 requirements.',
      atRisk: 'Supervisory visits may not be frequent enough. Review 3350-6.6.4.2 for specific requirements.',
      nonCompliant: 'On-site supervisory visits are not being conducted. This violates 3350-6.6.4.2.',
      unknown: 'Verify supervisory visit practices against 3350-6.6.4.2.',
    },
    stateNotes: [
      '3350-6.6.4.2 requires on-site RN supervisory visits',
      'Frequency based on patient needs and federal requirements',
      'Medicare requires every 14 days for skilled patients',
      'Document supervisory visits in patient record',
    ],
  },

  {
    id: 'DE-F3',
    itemId: 'F3',
    stateCode: 'DE',
    title: 'Clinical Records Requirements',
    description: 'Delaware requires comprehensive clinical records per 3350-6.7',
    severity: 'high',
    regulationCode: '3350-6.7',
    regulationName: 'Clinical Records',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do your clinical records contain all Delaware-required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'demographics', label: 'Patient demographics and contact information' },
          { value: 'emergency', label: 'Emergency contacts (family, friends, designated persons) (3350-6.7.1.1.8)' },
          { value: 'advance_directive', label: 'Advance health-care directive or statement (3350-6.7.1.7)' },
          { value: 'agreement', label: 'Written service agreement (3350-6.7.1.8)' },
          { value: 'rights_ack', label: 'Patient rights acknowledgment (3350-6.7.1.9)' },
          { value: 'care_plan', label: 'Plan of care' },
          { value: 'visit_notes', label: 'Visit documentation signed and dated' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per 3350-6.7.',
      atRisk: 'Clinical records may be missing required elements. Review 3350-6.7 requirements.',
      nonCompliant: 'Clinical records are missing critical elements. Review 3350-6.7 requirements.',
      unknown: 'Review clinical record contents against 3350-6.7.',
    },
    stateNotes: [
      'Notes must be signed and dated on day service is rendered (3350-6.7.3)',
      'Notes must be legibly written in ink or electronic, dated, signed with full name and title (3350-6.7.4)',
      'Notes must be incorporated into record at least every 30 days (3350-6.7.5)',
    ],
  },

  {
    id: 'DE-F4',
    itemId: 'F4',
    stateCode: 'DE',
    title: 'Written Service Agreements',
    description: 'Delaware requires written financial agreements between agency and patient',
    severity: 'high',
    regulationCode: '3350-6.7.1.8',
    regulationName: 'Service Agreement',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written service agreements with all patients that include:',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Description of services purchased and associated cost' },
          { value: 'payment', label: 'Acceptable method of payment' },
          { value: 'billing', label: 'Outline of billing procedures' },
        ],
        required: true,
        helpText: 'Delaware statute requires written financial agreements with these elements',
      },
      {
        id: 'q2',
        question: 'Are all patient payments made directly to the agency (not to individual employees)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Delaware requires payments to agency or billing representative, not individual employees',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Written service agreements meet Delaware requirements.',
      atRisk: 'Service agreements may be missing required elements. Review requirements.',
      nonCompliant: 'Service agreements are incomplete. Develop agreements with all required elements.',
      unknown: 'Review service agreement requirements.',
    },
    stateNotes: [
      '3350-6.7.1.8 requires written service agreements',
      'Must include services, costs, payment methods, billing procedures',
      'Payments must be made to agency, not individual employees',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'DE-G1',
    itemId: 'G1',
    stateCode: 'DE',
    title: 'Patient Rights',
    description: 'Delaware requires patient rights notification per 3350-7.0',
    severity: 'high',
    regulationCode: '3350-7.0',
    regulationName: 'Patient Rights',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign written acknowledgment that they have been fully informed of their rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-6.7.1.9 requires written acknowledgment in patient record',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets Delaware 3350-7.0 requirements.',
      atRisk: 'Patient rights acknowledgment is not documented. Implement signed acknowledgment.',
      nonCompliant: 'Patient rights are not being provided. This violates 3350-7.0.',
      unknown: 'Verify patient rights process meets 3350-7.0 requirements.',
    },
    stateNotes: [
      '3350-7.0 lists patient rights that must be provided',
      '3350-6.7.1.9 requires written acknowledgment in patient record',
      'Must include DHCQ complaint information',
    ],
  },

  {
    id: 'DE-G2',
    itemId: 'G2',
    stateCode: 'DE',
    title: 'Quality Improvement Program',
    description: 'Delaware requires ongoing quality improvement per 3350-8.0',
    severity: 'medium',
    regulationCode: '3350-8.0',
    regulationName: 'Quality Improvement',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented quality improvement program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which QI activities does your program include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'chart_review', label: 'Clinical record review' },
          { value: 'patient_satisfaction', label: 'Patient satisfaction surveys' },
          { value: 'incident_tracking', label: 'Incident tracking and analysis' },
          { value: 'performance_improvement', label: 'Performance improvement initiatives' },
          { value: 'outcomes', label: 'Patient outcomes monitoring' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets Delaware 3350-8.0 requirements.',
      atRisk: 'QI program needs additional components. Review 3350-8.0 requirements.',
      nonCompliant: 'No quality improvement program exists. This violates 3350-8.0.',
      unknown: 'Verify QI program meets 3350-8.0 requirements.',
    },
    stateNotes: [
      '3350-8.0 requires ongoing quality improvement program',
      'Include chart review, patient satisfaction, incident tracking',
      'Performance improvement initiatives required',
      'Report to governing body',
    ],
  },

  {
    id: 'DE-G3',
    itemId: 'G3',
    stateCode: 'DE',
    title: 'Disaster Preparedness',
    description: 'Delaware requires disaster preparedness planning per 3350-10.0',
    severity: 'medium',
    regulationCode: '3350-10.0',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Which emergency preparedness components does your agency have? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'plan', label: 'Written disaster preparedness plan' },
          { value: 'weather', label: 'Severe weather procedures (hurricanes, coastal flooding)' },
          { value: 'patient_priority', label: 'Patient priority lists for emergencies' },
          { value: 'communication', label: 'Emergency communication plan' },
          { value: 'training', label: 'Staff training on emergency procedures' },
          { value: 'drills', label: 'Regular emergency drills' },
        ],
        required: true,
        helpText: 'Delaware is subject to coastal weather emergencies',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness program meets Delaware 3350-10.0 requirements.',
      atRisk: 'Emergency preparedness program needs additional components. Consider Delaware-specific coastal hazards.',
      nonCompliant: 'Emergency preparedness is inadequate per 3350-10.0. Develop a comprehensive plan.',
      unknown: 'Review emergency preparedness against 3350-10.0 requirements.',
    },
    stateNotes: [
      '3350-10.0 requires disaster preparedness planning',
      'Delaware is subject to coastal weather emergencies (hurricanes, flooding)',
      'Include patient priority lists and communication plan',
      'Staff training and drills recommended',
    ],
  },

  {
    id: 'DE-G4',
    itemId: 'G4',
    stateCode: 'DE',
    title: 'Complaint and Grievance Procedures',
    description: 'Delaware requires formal complaint handling procedures',
    severity: 'medium',
    regulationCode: '3350-5.1.3.6',
    regulationName: 'Complaints',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'Do you document complaints and grievances relating to employee/contractor conduct?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3350-5.1.3.6 requires documentation of complaints and action taken',
      },
      {
        id: 'q2',
        question: 'Does the director conduct or supervise resolution of patient complaints?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-5.1.3.7 requires director involvement in complaint resolution',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint and grievance process meets Delaware requirements.',
      atRisk: 'Director involvement in complaint resolution should be documented.',
      nonCompliant: 'No complaint documentation process exists. Implement per 3350-5.1.3.6.',
      unknown: 'Review complaint and grievance procedures.',
    },
    stateNotes: [
      '3350-5.1.3.6 requires documentation of complaints and action taken',
      '3350-5.1.3.7 requires director involvement in complaint resolution',
      'Maintain complaint log for DHCQ review',
    ],
  },

  {
    id: 'DE-G5',
    itemId: 'G5',
    stateCode: 'DE',
    title: 'HIPAA Compliance Program',
    description: 'Federal HIPAA requirements apply to Delaware home health agencies',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Which HIPAA compliance components does your agency have? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'privacy_officer', label: 'Designated Privacy Officer' },
          { value: 'policies', label: 'Written HIPAA policies and procedures' },
          { value: 'training', label: 'Staff HIPAA training program' },
          { value: 'baa', label: 'Business Associate Agreements with vendors' },
          { value: 'breach', label: 'Breach notification procedures' },
          { value: 'access', label: 'Patient access to records procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program is comprehensive.',
      atRisk: 'HIPAA compliance program is missing key components. Review and enhance.',
      nonCompliant: 'Significant HIPAA compliance gaps exist. Develop comprehensive program immediately.',
      unknown: 'Review HIPAA compliance program for completeness.',
    },
    stateNotes: [
      '45 CFR 164 governs HIPAA requirements',
      'Privacy Officer designation required',
      'Annual staff training recommended',
      'Business Associate Agreements required with vendors',
    ],
  },

  {
    id: 'DE-G6',
    itemId: 'G6',
    stateCode: 'DE',
    title: 'Contracted Services Requirements',
    description: 'Delaware has specific requirements for contracted services per 3350-5.3',
    severity: 'medium',
    regulationCode: '3350-5.3',
    regulationName: 'Contracted Services',
    agencyName: 'Delaware Division of Health Care Quality',
    agencyAbbr: 'DHCQ',
    agencyPhone: '(302) 421-7400',
    agencyUrl: 'https://dhss.delaware.gov/dhcq/',
    questions: [
      {
        id: 'q1',
        question: 'If you use contracted personnel, do you have written contractual arrangements?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all contractors have written agreements' },
          { value: 'some', label: 'Some do, not all' },
          { value: 'no', label: 'No written agreements' },
          { value: 'na', label: 'N/A - no contracted personnel' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your contractor agreements include annual renewal clauses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3350-5.3.2.9 requires renewal clause or annual renewal',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services arrangements meet Delaware 3350-5.3 requirements.',
      atRisk: 'Some contractor agreements may be missing. Review all arrangements.',
      nonCompliant: 'Contracted personnel lack required written agreements. Develop per 3350-5.3.',
      unknown: 'Review contracted services requirements.',
    },
    stateNotes: [
      '3350-5.3 governs contracted services requirements',
      'Written contractual arrangements required',
      '3350-5.3.2.9 requires renewal clause or annual renewal',
      'Contractors must meet same qualification requirements as employees',
    ],
  },
];

export default DELAWARE_QUESTIONS;